import { useEffect } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

interface RouteErrorFallbackProps {
  error: Error;
  reset: () => void;
}

function isChunkLoadError(error: Error): boolean {
  const message = error.message ?? "";
  return (
    error.name === "ChunkLoadError" ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Loading chunk .* failed/i.test(message) ||
    /Loading CSS chunk .* failed/i.test(message) ||
    /error loading dynamically imported module/i.test(message)
  );
}

// Renders when a route fails to load — most often because a code-split
// chunk couldn't be fetched. The retry button asks the router to remount
// the route, which re-attempts the dynamic import.
export function RouteErrorFallback({ error, reset }: RouteErrorFallbackProps) {
  const chunkError = isChunkLoadError(error);
  useEffect(() => {
    if (!chunkError) console.error("[route-error]", error);
  }, [chunkError, error]);

  const title = chunkError
    ? "Couldn't load this screen"
    : "Something went wrong";
  const description = chunkError
    ? "There was a problem loading this part of the app. Try again in a moment."
    : "An unexpected error occurred. Try again or reload the page.";

  return (
    <div className="flex flex-col items-center justify-center h-full w-full py-12 px-4">
      <div className="flex flex-col items-center gap-4 text-center max-w-sm">
        <div className="rounded-full bg-muted p-6">
          <AlertTriangle className="h-12 w-12 text-muted-foreground" />
        </div>
        <div className="space-y-2">
          <h3 className="text-lg font-semibold">{title}</h3>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        <button
          type="button"
          onClick={reset}
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
        >
          <RefreshCw className="h-4 w-4" />
          Try again
        </button>
      </div>
    </div>
  );
}
